/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useGatewayList.ts
 🗓️ Created: 2025-01-17
 🧩 Description:
    Hook for managing Gateway list with DataGrid integration.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useMemo, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useDataGrid, type DataGridFetchResult } from '../../../hooks/useDataGrid';
import { useAuthStore } from '../../../store/auth';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useContextStore } from '../../../store/context';
import { fetchGateways } from '../api/gatewayApi';
import { Gateway, GatewayFetchParams } from '../types';

interface UseGatewayListOptions {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

/**
 * Hook for Gateway list management
 * Implements Domain Contextual Filtering pattern
 */
export function useGatewayList(options: UseGatewayListOptions = {}) {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = options.domainId ?? selectedDomain?.id;

  const fetchData = useCallback(async (params: {
    page: number;
    limit: number;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    search?: string;
  }): Promise<DataGridFetchResult> => {
    try {
      const fetchParams: GatewayFetchParams = {
        ...params,
        domainId: effectiveDomainId
      };

      const response = await fetchGateways(
        fetchParams,
        user?.tenant?.slug,
        user?.id ? Number(user.id) : undefined
      );
      return {
        data: response.data || [],
        pagination: response.pagination || {
          page: params.page,
          limit: params.limit,
          total: 0,
          totalPages: 0
        }
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('gateways.loadError');
      showError(message);
      throw error;
    }
  }, [showError, t, user?.id, user?.tenant?.slug, effectiveDomainId]);

  const availableColumns = useMemo(() => [
    { key: 'name', label: t('gateways.name'), visible: true, sortable: true },
    { key: 'username', label: t('gateways.username'), visible: true, sortable: false },
    { key: 'proxy', label: t('gateways.proxy'), visible: true, sortable: false },
    { key: 'register', label: t('gateways.register'), visible: true, sortable: false },
    { key: 'enabled', label: t('gateways.enabled'), visible: true, sortable: false },
    { key: 'description', label: t('gateways.description'), visible: false, sortable: false },
    { key: 'createdAt', label: t('gateways.createdAt'), visible: false, sortable: true },
    { key: 'actions', label: t('common.actions'), visible: true, sortable: false }
  ], [t]);

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh
  } = useDataGrid({
    fetchData,
    initialColumns: availableColumns,
    initialSortBy: 'name',
    initialSortOrder: 'asc',
    initialLimit: 10,
    moduleName: 'gateways',
    reloadKey: effectiveDomainId ?? null
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && options.onRefreshNeeded) {
      options.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data: data as Gateway[],
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId
  };
}

